import React from 'react';
import { monthLabel, monthHeader } from '../uso-de-shelf/usageHeat';
import { brand } from '../../theme/tokens';

// Canonical category key — MUST match the backend `_normCat` (lowercase, accents stripped,
// separators collapsed) so filter values line up with the server's `byCategory` keys.
export function normCat(s) {
    return (s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[_\s-]+/g, '_')
        .trim();
}

// Title-case a category label ("aceite" / "HARINA DE TRIGO" → "Aceite" / "Harina De Trigo").
export function titleize(s) {
    if (!s) return '';
    return String(s)
        .toLowerCase()
        .split(/\s+/)
        .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
        .join(' ');
}

// Heat palette anchors (pink → yellow → green), shared by SOS and DN.
const HEAT_PINK = [244, 151, 156];
const HEAT_YELLOW = [253, 224, 138];
const HEAT_GREEN = [150, 208, 158];
const HEAT_TEXT = '#334155';

const lerp = (a, b, t) => a.map((c, i) => Math.round(c + (b[i] - c) * t));
const rgb = (c) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

/**
 * Share-of-Shelf heat: a pink → yellow → green gradient anchored at 0% (pink), 10% (yellow) and
 * 25%+ (green) — SOS rarely exceeds a quarter of the shelf, so 25% is the "full green" ceiling.
 * Values between anchors are linearly interpolated.
 */
export function sosStyle(pct) {
    if (pct == null || Number.isNaN(pct)) return { bg: 'transparent', color: 'inherit' };
    let c;
    if (pct <= 0) c = HEAT_PINK;
    else if (pct >= 25) c = HEAT_GREEN;
    else if (pct <= 10) c = lerp(HEAT_PINK, HEAT_YELLOW, pct / 10);
    else c = lerp(HEAT_YELLOW, HEAT_GREEN, (pct - 10) / 15);
    return { bg: rgb(c), color: HEAT_TEXT };
}

/**
 * Numeric-Distribution heat: discrete rule bands (not a gradient) — <80% pink, 80–90% yellow,
 * ≥90% green — matching the reporting spec.
 */
export function dnStyle(pct) {
    if (pct == null || Number.isNaN(pct)) return { bg: 'transparent', color: 'inherit' };
    const c = pct < 80 ? HEAT_PINK : pct < 90 ? HEAT_YELLOW : HEAT_GREEN;
    return { bg: rgb(c), color: HEAT_TEXT };
}

// A percentage heat cell. `get(row)` returns a number (%) or null/undefined. `styleFn` picks the
// palette (SOS gradient by default, DN rule bands when passed).
export function sosCol({ title, keyName, get, bold = false, width = 90, styleFn = sosStyle }) {
    return {
        title,
        key: keyName || title,
        align: 'center',
        width,
        onCell: () => ({ style: { padding: 0 } }),
        render: (_, r) => {
            const v = get(r);
            if (v == null || Number.isNaN(v)) return <div className="heat-cell text-surface-300">—</div>;
            const { bg, color } = styleFn(v);
            return (
                <div className="heat-cell" style={{ background: bg, color, fontWeight: bold ? 700 : 600 }}>
                    {v.toFixed(1)}%
                </div>
            );
        }
    };
}

// One heat column per month (+ a Total column). Rows carry `byMonth`/`total`.
// `palette` = 'dn' switches to the Distribución Numérica rule bands; anything else = SOS gradient.
export function sosMonthColumns(months, palette) {
    const styleFn = palette === 'dn' ? dnStyle : sosStyle;
    const cols = (months || []).map((m) =>
        sosCol({ title: monthHeader(m), keyName: m, get: (r) => r.byMonth?.[m], styleFn })
    );
    cols.push(sosCol({ title: 'Total', keyName: '__total', get: (r) => r.total, bold: true, width: 100, styleFn }));
    return cols;
}

/**
 * Compact dependency-free SVG line for the "SOS x Mes" trend. `points` = [{ month, sos }].
 * Scales to 0–100; dots labeled with the value; a dashed target line is optional.
 */
export function SosTrend({ points = [], height = 150 }) {
    const pts = (points || []).filter((p) => p.sos != null);
    if (pts.length === 0) return <div className="py-6 text-center text-sm text-surface-400">Sin datos para el mes.</div>;

    const W = Math.max(320, pts.length * 120);
    const H = height;
    const padX = 44;
    const padY = 24;
    const innerW = W - padX * 2;
    const innerH = H - padY * 2;
    const x = (i) => (pts.length === 1 ? padX + innerW / 2 : padX + (innerW * i) / (pts.length - 1));
    const y = (v) => padY + innerH * (1 - Math.max(0, Math.min(100, v)) / 100);
    const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.sos).toFixed(1)}`).join(' ');
    const area = `${line} L ${x(pts.length - 1).toFixed(1)} ${(H - padY).toFixed(1)} L ${x(0).toFixed(1)} ${(H - padY).toFixed(1)} Z`;

    return (
        <div style={{ overflowX: 'auto' }}>
            <svg width={W} height={H} role="img" aria-label="SOS por mes">
                {[0, 25, 50, 75, 100].map((g) => (
                    <g key={g}>
                        <line x1={padX} y1={y(g)} x2={W - padX} y2={y(g)} stroke="#eef0f3" strokeWidth="1" />
                        <text x={padX - 8} y={y(g) + 3} textAnchor="end" fontSize="10" fill="#9aa3b2">{g}%</text>
                    </g>
                ))}
                <path d={area} fill="rgba(26,99,227,0.10)" stroke="none" />
                <path d={line} fill="none" stroke={brand[600]} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                {pts.map((p, i) => (
                    <g key={p.month}>
                        <circle cx={x(i)} cy={y(p.sos)} r="4" fill={brand[600]} stroke="#fff" strokeWidth="1.5" />
                        <text x={x(i)} y={y(p.sos) - 10} textAnchor="middle" fontSize="11" fontWeight="700" fill={brand[800]}>
                            {p.sos.toFixed(0)}%
                        </text>
                        <text x={x(i)} y={H - padY + 16} textAnchor="middle" fontSize="10" fill="#6b7585">
                            {monthLabel(p.month)}
                        </text>
                    </g>
                ))}
            </svg>
        </div>
    );
}
